import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useHostname } from "@/lib/ssr";
import { detectHostTenant } from "@/lib/hostTenant";
import { APP_PAGE_PATH, STUDIO_PAGE_PATH, canonicalQuery, findPublicPage, indexedLangs, type PublicPage } from "@/lib/seo/pages";
import { PERSONAL_APP_STORE_URL } from "@/pages/home/paths";
import { isNewHomeEnabled } from "@/lib/homeFlag";
import { extraStructuredData } from "@/lib/seo/structuredData";
import { imageObjects } from "@/lib/seo/images";
import { isGuidesEnabled } from "@/lib/guidesFlag";
import { isHelpEnabled } from "@/lib/helpFlag";
import { useHelpContentVersion } from "@/pages/help/helpLinks";
import {
  LANG_TAG,
  LOGO_PATH,
  OG_LOCALE,
  baseLang,
  SOCIAL_PROFILES,
  langFromSearch,
  localisedUrl,
  siteOrigin,
  type SeoLang,
} from "@/lib/seo/site";

/**
 * The full <head> for a public marketing page (src/lib/seo/pages.ts): title, description, canonical,
 * hreflang alternates, Open Graph + Twitter with a 1200x630 image, and JSON-LD. Rendered through
 * Helmet, so it is in the server's first HTML response, which is all a link unfurler (Facebook,
 * WhatsApp, Slack, X, LinkedIn, iMessage) or crawler reads.
 *
 * Rendered in AppShell AFTER the routes, so for these pages it wins over a page's own Helmet tags and
 * over DefaultMeta. It renders nothing on tenant hosts or for paths that aren't public pages (the
 * admin, the uploader, galleries keep their own head, e.g. GalleryMeta).
 *
 * The URL's language (?lang=es, which the server also renders in) sets the canonical, og:url and
 * og:locale, so they are stable for a URL whatever language a visitor has picked; the title and
 * description follow the language the page is shown in. On the server the two are the same.
 */

const organizationLd = (origin: string) => ({
  "@type": "Organization",
  "@id": `${origin}/#organization`,
  name: "Photohawk",
  url: `${origin}/`,
  logo: { "@type": "ImageObject", url: `${origin}${LOGO_PATH}`, width: 512, height: 512 },
  sameAs: SOCIAL_PROFILES,
});

// Helmet prints React's `hrefLang` as written; spell the attribute the way the HTML spec does.
const hreflang = (value: string) => ({ hreflang: value }) as object;

const pageLang = (page: PublicPage, lang: string | undefined): SeoLang => {
  const base = baseLang(lang);
  return (page.langs as readonly string[]).includes(base) ? (base as SeoLang) : "en";
};

const RouteMeta = () => {
  const { pathname, search } = useLocation();
  const { i18n } = useTranslation();
  const host = useHostname();
  // A help language that loads in the browser after navigation changes the page's title and description.
  useHelpContentVersion();
  if (detectHostTenant(host).type !== "app") return null;
  const newHome = isNewHomeEnabled(host);
  const page = findPublicPage(pathname, newHome, isGuidesEnabled(host), isHelpEnabled(host));
  if (!page) return null;

  const origin = siteOrigin(host);
  const lang = langFromSearch(search, page.langs) ?? "en";
  const shown = pageLang(page, i18n.language);
  const { title, description } = page.seo(shown);
  // A listing's own pages (/find-your-photos?country=gb&before=…) are their own canonical URLs.
  const query = canonicalQuery(page, search);
  const url = localisedUrl(origin, page.path, lang, query);
  const image = `${origin}${page.image.path}`;
  // Hreflang alternates: the languages search engines may index (a machine translation awaiting review renders, but
  // is noindex and isn't offered as an alternate).
  const alternates = indexedLangs(page);
  const localised = alternates.length > 1;
  const indexable = page.indexable && alternates.includes(lang);

  const graph: object[] = [
    organizationLd(origin),
    // A page with its own structured data (the guides: Article, BreadcrumbList, FAQPage) replaces the default node.
    ...(page.structuredData
      ? page.structuredData({ origin, url, lang: shown, title, description, image })
      : [
          {
            "@type": page.type === "article" ? "Article" : "WebPage",
            "@id": `${url}#page`,
            url,
            name: title,
            ...(page.type === "article" ? { headline: title } : {}),
            description,
            inLanguage: LANG_TAG[shown],
            // With images on the page: the link-preview card and each image as an ImageObject, the first one shown as
            // the page's primary image.
            ...(page.images?.length
              ? {
                  image: imageObjects(origin, page.image, page.images),
                  primaryImageOfPage: { "@type": "ImageObject", url: `${origin}${page.images[0].path}`, width: page.images[0].width, height: page.images[0].height },
                }
              : { image }),
            isPartOf: { "@type": "WebSite", "@id": `${origin}/#website`, name: "Photohawk", url: `${origin}/` },
            publisher: { "@id": `${origin}/#organization` },
          },
        ]),
  ];
  // Breadcrumbs, FAQPage (pages that show questions and answers) and the /pricing offers: src/lib/seo/structuredData.ts.
  graph.push(...extraStructuredData({ origin, path: page.path, url, lang: shown, title, description }));
  // The web app itself: "/" today; "/studio" (Photohawk Studio) on the new site, where "/" tells the story of both
  // products.
  if (page.path === (newHome ? STUDIO_PAGE_PATH : "/")) {
    graph.push({
      "@type": "SoftwareApplication",
      name: newHome ? "Photohawk Studio" : "Photohawk",
      url: newHome ? `${origin}${STUDIO_PAGE_PATH}` : `${origin}/`,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description,
      publisher: { "@id": `${origin}/#organization` },
    });
  }
  // The Photohawk app, on its own page (/app) on the new site: the iPhone app and its App Store listing.
  if (newHome && page.path === APP_PAGE_PATH) {
    graph.push({
      "@type": "MobileApplication",
      name: "Photohawk: Friends & Family",
      alternateName: "Photohawk",
      url: `${origin}${APP_PAGE_PATH}`,
      downloadUrl: PERSONAL_APP_STORE_URL,
      installUrl: PERSONAL_APP_STORE_URL,
      applicationCategory: "PhotographyApplication",
      operatingSystem: "iOS",
      description,
      publisher: { "@id": `${origin}/#organization` },
    });
  }
  const jsonLd = JSON.stringify({ "@context": "https://schema.org", "@graph": graph }).replace(/</g, "\\u003c");

  return (
    <Helmet>
      <html lang={LANG_TAG[shown]} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={indexable ? "index, follow, max-image-preview:large" : page.indexable ? "noindex, follow" : "noindex, nofollow"} />
      <link rel="canonical" href={url} />
      {localised &&
        alternates.map((l) => (
          <link key={l} rel="alternate" {...hreflang(LANG_TAG[l])} href={localisedUrl(origin, page.path, l, query)} />
        ))}
      {localised && <link rel="alternate" {...hreflang("x-default")} href={localisedUrl(origin, page.path, "en", query)} />}

      <meta property="og:site_name" content="Photohawk" />
      <meta property="og:type" content={page.type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:locale" content={OG_LOCALE[lang]} />
      {alternates
        .filter((l) => l !== lang)
        .map((l) => (
          <meta key={l} property="og:locale:alternate" content={OG_LOCALE[l]} />
        ))}
      <meta property="og:image" content={image} />
      <meta property="og:image:secure_url" content={image} />
      <meta property="og:image:type" content={page.image.type} />
      <meta property="og:image:width" content={String(page.image.width)} />
      <meta property="og:image:height" content={String(page.image.height)} />
      <meta property="og:image:alt" content={page.image.alt} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={page.image.alt} />

      <script type="application/ld+json">{jsonLd}</script>
    </Helmet>
  );
};

export default RouteMeta;
